<script>
import { groups } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { line, curveCardinal } from 'd3-shape';
import YAxis from './components/yAxis.svelte';
import Globe from './components/Globe.svelte';

let { width, data } = $props();
const margin = { left: 0, top: 14, right: 62, bottom: 28 };

// Strong "super" El Niño buildup years to highlight in red
const ninos = [1982, 1997, 2015];
const currentYear = 2026;

// Roughly April (week 14) through December (week 53)
const WEEK_START = 14;
const WEEK_END = 52;

// Approximate ISO week for the start of each month (non-leap year)
const months = [
    { label: 'Apr', week: 14 },
    { label: 'May', week: 18 },
    { label: 'Jun', week: 22 },
    { label: 'Jul', week: 27 },
    { label: 'Aug', week: 31 },
    { label: 'Sep', week: 35 },
    { label: 'Oct', week: 40 },
    { label: 'Nov', week: 44 },
    { label: 'Dec', week: 48 },
];

let height = $state();

let chartwidth = $derived(width - margin.left - margin.right);
let chartheight = $derived(height - margin.top - margin.bottom);

let filtered = $derived(
    data ? data.filter(d => +d.week >= WEEK_START && +d.week <= WEEK_END) : null
);

// Group by year and sort so gray background lines draw first, highlighted
// El Niño years next, and the current year last (on top).
let grouped = $derived.by(() => {
    if (!filtered) return null;
    const rank = d => (d.isCurrent ? 2 : d.isNino ? 1 : 0);
    return groups(filtered, d => +d.year)
        .map(([year, values]) => ({
            year,
            values: values.slice().sort((a, b) => +a.week - +b.week),
            isNino: ninos.includes(year),
            isCurrent: year === currentYear,
        }))
        .sort((a, b) => rank(a) - rank(b));
});

// Split lines so the axis can sit in front of the gray background years but
// behind the highlighted (red) years.
let backgroundSeries = $derived(grouped ? grouped.filter(s => !s.isNino && !s.isCurrent) : []);
let foregroundSeries = $derived(grouped ? grouped.filter(s => s.isNino || s.isCurrent) : []);

let xScale = $derived(
    chartwidth ? scaleLinear().domain([WEEK_START, WEEK_END]).range([0, chartwidth]) : null
);
let yScale = $derived(
    chartheight ? scaleLinear().domain([3.6, -2]).range([0, chartheight]) : null
);

let lineGen = $derived(
    xScale && yScale
        ? line()
              .x(d => xScale(+d.week))
              .y(d => yScale(+d.nino_ix))
              .curve(curveCardinal)
        : null
);

// End point of the current-year line, for the pulsing marker + label
let currentSeries = $derived(grouped ? grouped.find(d => d.isCurrent) : null);
let lastPoint = $derived(
    currentSeries && currentSeries.values.length
        ? currentSeries.values[currentSeries.values.length - 1]
        : null
);

const fmt = v => (v > 0 ? '+' : '') + v.toFixed(1);

// Label an El Niño season spanning two years, e.g. 1982 -> "1982-83"
const seasonLabel = y => `${y}-${String(y + 1).slice(-2)}`;

// Parse as a local date to avoid timezone shifting the day
const parseDate = d => {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day);
};

const fmtDate = d =>
    d
        ? parseDate(d).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
          })
        : '';

const fmtShortDate = d =>
    d
        ? parseDate(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';

// Hover state
let svgEl = $state();
let hovered = $state(null); // hovered series
let hoverPoint = $state(null); // nearest data point under the cursor
let pointer = $state({ x: 0, y: 0 }); // cursor position for the tooltip

// Flip the tooltip to the left of the cursor near the right edge so it
// doesn't get clipped.
let tooltipFlip = $derived(width ? pointer.x > width * 0.6 : false);

// End-of-line labels for the highlighted El Niño years (the current year
// already has its own marker + label).
// Highlighted years whose end-of-line label is suppressed to avoid crowding
const hiddenLabels = [];

let ninoLabels = $derived(
    grouped && xScale && yScale
        ? grouped
              .filter(s => s.isNino && s.values.length && !hiddenLabels.includes(s.year))
              .map(s => {
                  const last = s.values[s.values.length - 1];
                  return { year: s.year, x: xScale(+last.week), y: yScale(+last.nino_ix) };
              })
        : []
);

function handleHover(e, s) {
    if (!svgEl || !xScale) return;
    const rect = svgEl.getBoundingClientRect();
    const week = xScale.invert(e.clientX - rect.left - margin.left);
    let nearest = s.values[0];
    let best = Infinity;
    for (const p of s.values) {
        const dist = Math.abs(+p.week - week);
        if (dist < best) {
            best = dist;
            nearest = p;
        }
    }
    hovered = s;
    hoverPoint = nearest;
    pointer = { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

function clearHover() {
    hovered = null;
    hoverPoint = null;
}
</script>


<div class="graphic-wrapper" bind:clientWidth={width} bind:clientHeight={height}>
    <div class="locator">
        <Globe size={84} />
    </div>

    <div class="legend">
        <span class="legend-line"></span>
        <span class="legend-label">Top 3 strongest El Niños</span>
    </div>
    {#if width && height}
        <svg bind:this={svgEl} {width} {height}>
            <g class="chart" transform="translate({margin.left}, {margin.top})">
                {#if yScale}
                    <rect
                        class="nino-band"
                        x="0"
                        y="0"
                        width={chartwidth}
                        height={yScale(0.5)}
                    ></rect>
                {/if}

                {#if xScale}
                    <g class="x-axis">
                        {#each months as m}
                            <text
                                class="month-label"
                                class:start={m.week === WEEK_START}
                                class:hide-small={m.week === WEEK_START && width < 450}
                                x={xScale(m.week)}
                                y={chartheight + 16}
                            >{m.label}</text>
                        {/each}
                    </g>
                {/if}

                {#if grouped && lineGen}
                    <g class="lines" class:dim={hovered}>
                        {#each backgroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if yScale}
                        <YAxis {yScale} {chartwidth} />
                    {/if}

                    <g class="lines" class:dim={hovered}>
                        {#each foregroundSeries as s (s.year)}
                            <path
                                d={lineGen(s.values)}
                                class="year-line"
                                class:nino={s.isNino}
                                class:current={s.isCurrent}
                                fill="none"
                            ></path>
                        {/each}
                    </g>

                    {#if hovered}
                        <path
                            class="hover-line"
                            class:nino={hovered.isNino}
                            class:current={hovered.isCurrent}
                            d={lineGen(hovered.values)}
                            fill="none"
                        ></path>
                    {/if}

                    {#if yScale}
                        <text class="nino-band-label halo" x={chartwidth} y={yScale(0.5) - 6}>El Niño conditions</text>
                        <text class="nino-band-label" x={chartwidth} y={yScale(0.5) - 6}>El Niño conditions</text>
                    {/if}

                    {#each ninoLabels as l (l.year)}
                        <text class="year-label" x={l.x + 6} y={l.y + 4}>{seasonLabel(l.year)}</text>
                    {/each}

                    {#each grouped as s (s.year)}
                        <path
                            class="hit-area"
                            d={lineGen(s.values)}
                            fill="none"
                            role="presentation"
                            onpointerenter={(e) => handleHover(e, s)}
                            onpointermove={(e) => handleHover(e, s)}
                            onpointerleave={clearHover}
                        ></path>
                    {/each}
                {/if}

                {#if hoverPoint && xScale && yScale}
                    <circle
                        class="hover-dot"
                        class:nino={hovered && hovered.isNino}
                        class:current={hovered && hovered.isCurrent}
                        cx={xScale(+hoverPoint.week)}
                        cy={yScale(+hoverPoint.nino_ix)}
                        r="4"
                    ></circle>
                {/if}

                {#if lastPoint && xScale && yScale}
                    {@const cx = xScale(+lastPoint.week)}
                    {@const cy = yScale(+lastPoint.nino_ix)}
                    <g class="current-marker" transform="translate({cx}, {cy})">
                        <circle class="pulse" r="5"></circle>
                        <circle class="dot" r="4"></circle>
                        <text class="marker-year halo" x="11" y="-2">{fmtDate(lastPoint.date)}</text>
                        <text class="marker-year" x="11" y="-2">{fmtDate(lastPoint.date)}</text>
                        <text class="marker-value halo" x="11" y="12">{fmt(+lastPoint.nino_ix)}°C</text>
                        <text class="marker-value" x="11" y="12">{fmt(+lastPoint.nino_ix)}°C</text>
                    </g>
                {/if}
            </g>
        </svg>

        {#if hovered && hoverPoint}
            <div class="tooltip" class:flip={tooltipFlip} style="left:{pointer.x}px; top:{pointer.y}px;">
                <span class="tt-year">{seasonLabel(hovered.year)}</span>
                <span class="tt-date">{fmtShortDate(hoverPoint.date)}</span>
                <span class="tt-val">{fmt(+hoverPoint.nino_ix)}°C</span>
            </div>
        {/if}
    {/if}
</div>

<style lang='scss'>
    @import '@/styles/variables';

    .graphic-wrapper {
        width: 100%;
        position: relative;
        height: 500px;
    }

    .locator {
        position: absolute;
        top: 18px;
        left: 30px;
        z-index: 3;
        pointer-events: none;

        @media screen and (max-width: 450px) {
            top: 40px;
        }
    }

    .legend {
        position: absolute;
        top: 18px;
        right: 58px;
        z-index: 3;
        display: flex;
        align-items: center;
        gap: 6px;
        pointer-events: none;
    }

    .legend-line {
        display: inline-block;
        width: 18px;
        height: 1.75px;
        background: $red;
    }

    .legend-label {
        font-size: 12px;
        font-weight: 600;
        color: $gray11;
    }

    .nino-band {
        fill: $red;
        opacity: 0.07;
        pointer-events: none;
    }

    .nino-band-label {
        fill: $red;
        font-size: 12px;
        font-weight: 600;
        text-anchor: end;
        pointer-events: none;

        &.halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    .year-line {
        stroke: $gray4;
        stroke-width: 1;
        pointer-events: none;

        &.nino {
            stroke: $red;
            stroke-width: 1.75;
        }

        &.current {
            stroke: $cnn-red;
            stroke-width: 2.5;
        }
    }

    // Fade the whole field while hovering so the active line stands out
    .lines.dim .year-line {
        opacity: 0.28;
        transition: opacity 0.12s ease;
    }

    .hover-line {
        fill: none;
        stroke: $gray9;
        stroke-width: 2;
        pointer-events: none;

        &.nino {
            stroke: $red;
            stroke-width: 2.5;
        }

        &.current {
            stroke: $cnn-red;
            stroke-width: 3;
        }
    }

    .hit-area {
        fill: none;
        stroke: transparent;
        stroke-width: 12;
        pointer-events: stroke;
        cursor: crosshair;
    }

    .hover-dot {
        fill: $gray9;
        stroke: #fff;
        stroke-width: 1.5;
        pointer-events: none;

        &.nino {
            fill: $red;
        }

        &.current {
            fill: $cnn-red;
        }
    }

    .year-label {
        font-size: 12px;
        font-weight: 700;
        fill: $red;
        pointer-events: none;
    }

    .month-label {
        font-size: 11px;
        fill: #666;
        text-anchor: middle;
        pointer-events: none;

        &.start {
            text-anchor: start;
        }

        &.hide-small {
            display: none;
        }
    }

    .current-marker {
        pointer-events: none;

        .pulse {
            fill: $cnn-red;
            transform-box: fill-box;
            transform-origin: center;
            animation: pulse 1.6s ease-out infinite;
        }

        .dot {
            fill: $cnn-red;
        }

        .marker-year {
            font-size: 12px;
            font-weight: 700;
            fill: $cnn-red;
        }

        .marker-value {
            font-size: 12px;
            fill: $gray9;
        }

        .halo {
            fill: #fff;
            stroke: #fff;
            stroke-width: 3.5;
            stroke-linejoin: round;
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            opacity: 0.55;
        }
        100% {
            transform: scale(2.6);
            opacity: 0;
        }
    }

    .tooltip {
        position: absolute;
        pointer-events: none;
        transform: translate(12px, -50%);
        background: #fff;
        border: 1px solid $gray4;
        border-radius: 3px;
        padding: 4px 8px;
        font-size: 12px;
        line-height: 1.2;
        white-space: nowrap;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
        z-index: 4;

        &.flip {
            transform: translate(calc(-100% - 12px), -50%);
        }

        .tt-year {
            font-weight: 700;
            color: $gray11;
        }

        .tt-date {
            margin-left: 6px;
            color: $gray7;
        }

        .tt-val {
            margin-left: 6px;
            color: $gray9;
        }
    }
</style>
